import { Return } from "@redotech/redo-model/return";
import {
  RedoBadge,
  type RedoBadgeColor,
  type RedoBadgeProps,
} from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@redotech/redo-web/components/ui/tooltip";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useLayoutEffect, useRef, useState } from "react";
import { pillThemeToBadgeColor } from "./tags/tag-colors";

export interface BadgeCellItem {
  text: string;
  color: RedoBadgeColor;
  dataTestId?: string;
  segmentLeading?: RedoBadgeProps["segmentLeading"];
}

const BADGE_GAP = 4;

export const BadgeOverflowCell = memo(function BadgeOverflowCell({
  badges,
  emptyText = "-",
}: {
  badges: BadgeCellItem[];
  emptyText?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(badges.length);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) {
      return;
    }

    const compute = () => {
      const available = container.offsetWidth;
      const children = Array.from(measure.children) as HTMLElement[];
      // Last child is the overflow badge used for width measurement
      const overflowWidth = children[children.length - 1]?.offsetWidth ?? 0;

      let total = 0;
      let count = 0;

      for (let i = 0; i < badges.length; i++) {
        const child = children[i];
        if (!child) {
          break;
        }
        const w = child.offsetWidth + (i > 0 ? BADGE_GAP : 0);
        const isLast = i === badges.length - 1;

        if (isLast) {
          if (total + w <= available) {
            count = i + 1;
          }
        } else if (total + w + BADGE_GAP + overflowWidth <= available) {
          total += w;
          count = i + 1;
        } else {
          break;
        }
      }

      setVisibleCount(count);
    };

    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(container);
    return () => observer.disconnect();
  }, [badges]);

  if (badges.length === 0) {
    return <div>{emptyText}</div>;
  }

  const visibleBadges = badges.slice(0, visibleCount);
  const hiddenBadges = badges.slice(visibleCount);

  return (
    <div className="relative" ref={containerRef}>
      {/* Hidden measurement container: all badges + overflow badge */}
      <Flex
        className="pointer-events-none absolute invisible"
        gap="xs"
        ref={measureRef}
      >
        {badges.map((badge) => (
          <RedoBadge
            color={badge.color}
            key={badge.text}
            segmentLeading={badge.segmentLeading}
            size="sm"
            text={badge.text}
          />
        ))}
        <RedoBadge color="gray" size="sm" text={`+${badges.length}`} />
      </Flex>

      {/* Visible content */}
      <Flex className="overflow-hidden" gap="xs">
        {visibleBadges.map((badge) => (
          <RedoBadge
            color={badge.color}
            dataTestId={badge.dataTestId}
            key={badge.text}
            segmentLeading={badge.segmentLeading}
            size="sm"
            text={badge.text}
          />
        ))}
        {hiddenBadges.length > 0 && (
          <Tooltip>
            <TooltipTrigger
              className="cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            >
              <RedoBadge
                color="gray"
                size="sm"
                text={`+${hiddenBadges.length}`}
              />
            </TooltipTrigger>
            <TooltipContent
              hideArrow
              onClick={(e) => e.stopPropagation()}
              side="bottom"
              sideOffset={4}
            >
              <Flex className="max-w-60" gap="sm" wrap="wrap">
                {hiddenBadges.map((badge) => (
                  <RedoBadge
                    color={badge.color}
                    dataTestId={badge.dataTestId}
                    key={badge.text}
                    segmentLeading={badge.segmentLeading}
                    size="sm"
                    text={badge.text}
                  />
                ))}
              </Flex>
            </TooltipContent>
          </Tooltip>
        )}
      </Flex>
    </div>
  );
});

export const ReturnsTableTagsCell = memo(function ReturnsTableTagsCell({
  return_: returnData,
}: {
  return_: Return;
}) {
  const tags = returnData.tags || [];
  const badges: BadgeCellItem[] = tags.map((tag) => ({
    text: tag.name,
    color: pillThemeToBadgeColor[tag.pillTheme],
    dataTestId: `return-table-tag-${tag.name}`,
  }));

  return <BadgeOverflowCell badges={badges} />;
});
